import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, IconButton, Menu, MenuItem, Stack, Typography,
} from '@mui/material'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import useWallets from '@hooks/useWallets'
import useSymbolNames from '@hooks/useSymbolNames'
import {
  Link, LinkLabel, RunLabel, accountOf, canRequestHaltClear, labelOfExchange, marketOf,
  positionsOf, regimeOf, runStateLabel, settlementOf, sizingOf, statusChangeIntent,
  unrealizedOf,
} from '@util/wallet'

/**
 * 지갑 화면.
 *
 * 사람이 여기서 하는 일은 둘이다 -- **정지된 지갑에 "다 정리했다"고 신고하는 것**과
 * **가동 상태를 바꾸는 것**. 나머지는 전부 읽기다. 그래서 이 화면의 어려움은 버튼이 아니라
 * "지금 무슨 상태인지 헷갈리지 않게 보여주는 것"에 있다 -- 아래 셋은 서로 다른 사건인데
 * 겉보기가 비슷하다.
 *
 *   트레이더가 안 뜸 / 브로커 연결이 끊김 / 정지됨
 *
 * 한 칸에 접으면 사람이 엉뚱한 조치를 한다. 그래서 뱃지가 세 개다.
 *
 * 판정은 전부 `@util/wallet`에 있고 데이터는 전부 `@hooks/useWallets`에 있다. 이 파일은
 * 조립과 문구만 맡는다 -- 여기서 조건을 다시 계산하기 시작하면 그 규칙이 곧 복사된다.
 */

const num = (v, digits = 4) =>
  (v === null || v === undefined) ? '—' : Number(v).toFixed(digits)

/** 돈/가격은 자릿수를 끊어 준다 -- 62150.3과 6215.03을 한눈에 가르는 건 쉼표뿐이다. */
const money = (v, digits = 2) =>
  (v === null || v === undefined)
    ? '—'
    : Number(v).toLocaleString('ko-KR', { minimumFractionDigits: digits, maximumFractionDigits: digits })

/** 비율 칸은 % 를 괄호로 덧붙인다. **값을 바꾸지 않는다** -- 원문을 그대로 두고 덧붙일 뿐이다.
 *
 * `Ratio` 규약이 소수(0.02 = 2%)라 소수만 보이면 2%인지 200%인지 눈으로 안 갈린다.
 * `usc.page.js`가 같은 이유로 같은 모양을 쓰고 있었다 -- 칸이 옮겨왔으니 그 표기도 따라온다. */
const capText = (c) =>
  c.unit === 'ratio'
    ? `${num(c.value, 4)} (${(c.value * 100).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}%)`
    : money(c.value)

/** 손익·등락은 **부호를 항상 붙인다.** 양수에 +가 없으면 "0에서 얼마"인지가 안 읽힌다. */
const signed = (v, digits = 2) =>
  (v === null || v === undefined) ? '—' : `${v >= 0 ? '+' : ''}${money(v, digits)}`

/** 색은 부호로만 정한다 -- 0은 중립이다(이익도 손실도 아니다). */
/** 화면이 문자열을 손으로 적지 않게 한다 -- 값 공간 정본은 서버의 `RunStatuses` 이고
 * 허용 목록은 `/wallet/status` 로 받아온다. 여기 필요한 것은 "정상인가" 한 물음뿐이라
 * 상수 하나만 둔다(목록을 또 적으면 정본이 셋이 된다). */
const RUN_ACTIVE = 'ACTIVE'

const pnlColor = (v) => {
  if (v === null || v === undefined || v === 0) {
    return 'text.primary'
  }
  return v > 0 ? 'success.main' : 'error.main'
}

const ago = (ms) => {
  if (!ms) {
    return ''
  }
  const sec = Math.floor((Date.now() - ms) / 1000)
  if (sec < 60) {
    return `${sec}초 전`
  }
  if (sec < 3600) {
    return `${Math.floor(sec / 60)}분 전`
  }
  return `${Math.floor(sec / 3600)}시간 전`
}

const linkColor = (link) => {
  if (link === Link.OK) {
    return 'success'
  }
  if (link === Link.DOWN) {
    return 'error'
  }
  return 'warning'
}

const WalletPage = () => {
  const navigate = useNavigate()
  const { userId } = useParams()
  const {
    wallets, orphans, totals, connected, serviceError,
    loading, error, busyId, statuses, requestHaltClear, updateStatus, updateSymbolStatus,
  } = useWallets(userId)

  // 이름 사전. **자리 목록과 따로 온다** -- 자리의 정본은 `wallet_symbols` 라 트레이더가
  // 죽어도 줄이 서고, 그때도 이름이 붙어야 한다(`SymbolController` 머리글).
  const { symbolTextOf } = useSymbolNames()

  /**
   * 확인 대기 중인 status 변경. `null`이면 창이 닫힌 것이다.
   *
   * **선택과 반영을 갈라놓는 장치다.** 드롭다운의 `value`는 끝까지 서버 값(`run`)에 묶여
   * 있고, 고르는 행위는 여기에만 담긴다. 그래서 확인을 취소하거나 요청이 실패해도
   * 되돌릴 것이 없다 -- 화면은 애초에 안 바뀐 상태다.
   *
   * `{ walletId, next, intent }` 꼴. `intent`를 열 때 한 번 계산해 담아두는 이유는, 창이
   * 떠 있는 동안 스냅샷이 갱신되면(5초) 문구가 눈앞에서 바뀌기 때문이다 -- 사람이 읽던
   * 문장이 바뀌는 건 확인 창에서 제일 나쁜 일이다.
   */
  const [pending, set_pending] = useState(null)

  /**
   * 열려 있는 가동상태 메뉴. `{ el, walletId }` 꼴이고 `null`이면 닫힌 것이다.
   *
   * **지갑 객체가 아니라 id를 담는다.** 스냅샷이 5초마다 갈리므로 객체를 쥐고 있으면
   * 메뉴를 열어둔 사이 낡은 `run`으로 판정하게 된다 -- 고르는 순간 목록에서 다시 찾는다.
   */
  const [menu, set_menu] = useState(null)
  const menuWallet = menu ? wallets.find((w) => w.walletId === menu.walletId) : null
  /** 자리 메뉴면 그 자리, 지갑 메뉴면 `null`. **메뉴 하나를 두 층이 돌려 쓴다** --
   * 한 번에 하나만 열리고, 층마다 Menu 를 그리면 목록 길이만큼 무거워진다. */
  const menuSymbol = (menu && menu.symbolId != null && menuWallet)
    ? menuWallet.symbols.find((sy) => sy.symbolId === menu.symbolId)
    : null
  /** 지금 메뉴가 겨누는 값. 선택 표시(`selected`)가 이걸 본다. */
  const menuRun = menuSymbol ? menuSymbol.run : menuWallet?.run

  const closeMenu = () => set_menu(null)

  const closePending = () => set_pending(null)

  const confirmPending = async () => {
    if (!pending) {
      return
    }
    const { walletId, symbolId, next } = pending
    // 먼저 닫는다 -- 요청이 도는 동안 창을 열어두면 확인 버튼을 두 번 누를 수 있다.
    // 진행 표시는 아래 드롭다운이 busyId로 잠기는 것으로 대신한다.
    closePending()
    if (symbolId != null) {
      await updateSymbolStatus(walletId, symbolId, next)
    }
    else {
      await updateStatus(walletId, next)
    }
  }

  /** 정지 안내 -- 정지된 지갑에만 그린다.
   *
   * 거절 사유를 **원문 그대로** 보여준다. 트레이더가 쓰는 문장에 "1분 안에 자동 정리되니
   * 그때 다시 요청하라" 같은 다음 행동이 이미 들어 있어서, 요약하면 그게 날아간다. */
  const renderHalt = (w, sy) => {
    const { halt, link } = sy
    const gate = canRequestHaltClear(link, halt)
    // 잠금이 **자리 단위다** -- 지갑 id로 잠그면 한 자리를 누르는 동안 나머지 넷의 버튼까지
    // 같이 죽는다. 정지는 자리마다 따로 나는 사건이다.
    const busyKey = `${w.walletId}/${sy.symbolId}`

    return (
      <Alert severity='error' sx={{ mt: 1 }}>
        <Typography variant='body2'>
          <b>거래 정지 -- {halt.code}</b> {halt.at ? `(${ago(halt.at)})` : ''}
        </Typography>

        {/* 정지 사유 원문. 거절된 뒤에는 이 칸이 거절 사유로 덮여 있다(같은 컬럼을 쓴다). */}
        {halt.detail && (
          <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
            {halt.detail}
          </Typography>
        )}

        {halt.pending && (
          <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
            해제 요청 접수됨 ({ago(halt.reqAt)}) -- 판정 대기 중
          </Typography>
        )}

        {halt.rejectedReason && !halt.pending && (
          <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
            직전 해제 요청이 거절됐습니다.
            {halt.restartRequired
              ? ' 이 사유는 재기동으로만 풀립니다.'
              : ' 사유를 해소한 뒤 다시 요청하세요.'}
          </Typography>
        )}

        <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1 }}>
          <Button
            color='error'
            disabled={!gate.ok || busyId === busyKey}
            onClick={() => requestHaltClear(w.walletId, sy.symbolId)}
          >
            {busyId === busyKey ? '요청 중...' : '해제 요청'}
          </Button>
          {/* 비활성 버튼만 있고 왜 그런지가 없으면 사람은 화면이 고장 난 줄 안다. */}
          {!gate.ok && gate.why && (
            <Typography variant='caption'>{gate.why}</Typography>
          )}
        </Stack>
      </Alert>
    )
  }

  /** 메뉴에서 상태를 고른 뒤. 고르는 즉시 쏘지 않는다 -- 확인 창을 열 뿐이고 요청은 거기서 나간다. */
  const pickStatus = (next) => {
    const w = menuWallet
    const sy = menuSymbol
    closeMenu()
    if (!w) {
      return
    }

    // [2단 게이트, 2026-08-27] 자리 메뉴면 자리의 축으로 판정한다 -- 연결도 정지도 그 자리 것이다.
    if (sy) {
      if (next === sy.run) {
        return
      }
      set_pending({
        walletId: w.walletId,
        symbolId: sy.symbolId,
        next,
        // **범위를 문장으로 적는다.** 같은 확인 창이 지갑과 자리 둘 다에 뜨는데, FIN 문구가
        // "전부 종료합니다"라 무엇이 전부인지가 창에 없으면 사람이 지갑을 비우는 줄 안다.
        scope: `이 자리 하나입니다 -- ${w.row.name} ${symbolTextOf(sy.symbolId)}`,
        intent: statusChangeIntent(next, sy.link, sy.halt, sy.run),
      })
      return
    }

    if (next === w.run) {
      return
    }
    set_pending({
      walletId: w.walletId,
      next,
      scope: `이 지갑의 자리 ${w.symbols.length}개 전부입니다 -- ${w.row.name}`,
      // 지갑 상태를 바꾸는 판정이라 정지 여부도 **지갑 축으로 접는다** -- 자리 하나라도
      // 정지면 FIN 안내가 그 사실을 말해야 한다(청산이 안 나갈 수 있는 자리가 있다는 뜻).
      intent: statusChangeIntent(next, w.link, { halted: w.haltedCount > 0 }, w.run),
    })
  }

  /**
   * 가동 상태 -- 읽기와 쓰기를 **같은 자리**에 둔다.
   *
   * **뱃지 모양을 쓴다.** 이 줄에는 링크·정지 뱃지가 같이 서는데 드롭다운 박스만 높이가
   * 배였다. 한 줄 안에서 키가 다른 칸은 그 칸이 제일 중요하다는 뜻으로 읽히는데, 여기서
   * 중요한 건 셋을 **나란히 비교하는 것**이다. 그래서 생김새를 옆과 맞추고 화살표로만
   * "누를 수 있다"를 말한다.
   *
   * **텍스트 입력을 두지 않는다.** 값이 경로에 실려 서버로 가고, 오타가 통과하면
   * `rejectedByStatus`가 조용히 진입만 막는다(서버도 거르지만 화면이 오타를 낼 이유가 없다).
   * 고를 수 있는 값은 서버가 준 목록뿐이다.
   *
   * 목록을 못 받았으면 **화살표를 떼고 읽기 전용으로 되돌아간다** -- 눌러도 빈 메뉴만
   * 뜨는 칩은 화면이 고장 난 것으로 읽힌다. 값 자체는 DB에서 왔으므로 보여주는 데는
   * 문제가 없다.
   */
  const renderRun = (w, settle) => {
    const { run } = w
    // **고를 이름과 보여줄 이름이 다르다**(`RunLabel` vs `runStateLabel`). FIN은 정리 중과
    // 종료됨이 갈리는데, 메뉴 항목은 도달할 상태 하나여야 한다 -- 근거는 util 쪽 주석.
    const label = runStateLabel(run, settle)
    const color = run === 'ACTIVE' ? 'default' : 'warning'

    if (statuses.length === 0) {
      return <Chip size='small' label={label} color={color} variant='outlined' />
    }

    return (
      <Chip
        size='small'
        color={color}
        variant='outlined'
        disabled={busyId === w.walletId}
        onClick={(e) => set_menu({ el: e.currentTarget, walletId: w.walletId })}
        sx={{ cursor: 'pointer' }}
        label={
          <Stack direction='row' alignItems='center' component='span'>
            {label}
            {/* 화살표가 이 칩과 옆 뱃지를 가르는 유일한 표시다 -- 빼면 누를 수 있다는 걸 모른다. */}
            <ArrowDropDownIcon sx={{ fontSize: 16, ml: 0.25, mr: -0.75 }} />
          </Stack>
        }
      />
    )
  }

  /**
   * 종료된 지갑 안내 + 거래소 실물.
   *
   * **"포지션 없음"과 나란히 두면 안 되는 정보다.** 종료된 지갑의 `position`은 항상 비어
   * 있는데(엔진 것이 없다는 뜻), 거래소엔 사람이 연 게 있을 수 있다. 그 둘을 같은 자리에
   * 그리면 "이 지갑이 포지션을 들고 있다"로 읽힌다 -- FIN의 정의를 화면이 뒤집는 것이다.
   *
   * 그래서 별도 박스에, **"엔진 소유가 아니다"를 문장으로** 적는다. 색도 info다 -- 사고가
   * 아니라 사실이라 error/warning을 쓰면 사람이 조치를 찾는다.
   */
  const renderSettled = (settle) => {
    if (!settle.settled) {
      return null
    }
    return (
      <Alert severity='info' sx={{ mt: 1 }}>
        <Typography variant='body2'>
          <b>종료됨</b> -- 이 지갑의 거래는 더 이상 하지 않습니다(관측만 합니다)
        </Typography>

        {settle.has ? (
          <>
            <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
              거래소에 포지션이 있습니다 -- <b>엔진이 연 것이 아닙니다.</b> 트레이더는 관여하지 않습니다.
            </Typography>
            <Typography
              variant='body2'
              sx={{ mt: 0.5, opacity: settle.stale ? 0.45 : 1 }}
            >
              {settle.side} {num(settle.qty, 8)} @ {money(settle.entryPrice)}
              {settle.markPrice != null && ` · mark ${money(settle.markPrice)}`}
              {settle.pnl != null && (
                <Typography component='span' variant='body2' color={pnlColor(settle.pnl)}>
                  {` · ${signed(settle.pnl)}`}
                </Typography>
              )}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {ago(settle.at)} 관측{settle.stale ? ' · 조회 지연' : ''}
            </Typography>
          </>
        ) : (
          <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
            거래소에도 포지션이 없습니다 ({settle.at ? `${ago(settle.at)} 관측` : '관측 대기'})
          </Typography>
        )}
      </Alert>
    )
  }

  const renderPosition = (position) => {
    if (!position) {
      return <Typography variant='body2'>포지션 없음</Typography>
    }
    return (
      <Typography variant='body2'>
        {/* [자리 진입가, 2026-09-05] **이 자리 자신의 체결가다 -- 거래소 평단이 아니다.**
            옛 판은 평단을 그렸고, 자리가 둘이면 두 줄이 같은 진입가를 말했다. 게다가 그 값은
            어느 자리의 진입가도 아니었다(BNB 4h 712.13 · 1h 739.93 이 둘 다 731.28 로 나왔다).
            근거는 트레이더 `slotEntryPrices` 머리글.

            못 받았으면 `—` 다. 옆의 등락과 명목도 같이 빠지므로 **줄 하나가 통째로 모른다고
            말한다** -- 진입가만 비고 등락이 남으면 그 등락이 무엇 대비인지 읽을 데가 없다. */}
        {position.side} {num(position.qty, 8)} @ {money(position.slotEntryPrice)}
        {/* [2026-08-28 주인 지시] 진입 명목. 계산은 `positionsOf` 가 한다 -- 여기서 곱하면
            그 규칙이 곧 복사된다(이 파일 머리글).

            **못 구하면 칸을 통째로 뺀다.** `= —` 를 그리면 "명목이 0" 이나 "계산 실패" 로
            읽히는데, 사실은 수량이나 진입가가 아직 안 온 것뿐이고 그건 옆 칸이 이미 말한다. */}
        {position.notional == null ? '' : ` = ${money(position.notional)}`}
        {' '}stop={money(position.stopPrice)}
        {position.trailPrice ? ` trail=${money(position.trailPrice)}` : ''}
        {' '}({position.strategyName}/{position.signalTf})
      </Typography>
    )
  }

  /**
   * 자리 줄의 등락 -- **진입가 대비다.**
   *
   * 자리마다 진입가가 다르므로 이 값도 자리마다 하나다. 한 심볼에 4h 껍질과 1h 얹기가 같이
   * 서면 두 줄이 나온다.
   *
   * ⚠ **아래 `renderUnrealized`와 분모도 기준 시각도 다르다.** 저쪽은 잔고 대비이고 거래소
   * mark price 기준(`unrealizedAt`), 이쪽은 진입가 대비이고 마지막 봉 종가 기준이다
   * (`priceAt` = 그 봉이 닫힌 시각). 그래서 두 값을 한 줄에 합치지 않고, 저쪽에는 분모 이름을
   * 붙여 그린다.
   *
   * 낡았다고 값을 지우지 않는다. 지우면 사람이 "0이 됐다"로 읽는다 -- 흐리게 하는 게 맞다.
   */
  const renderChange = (market, pos) => {
    if (pos.changePct == null) {
      return null
    }
    return (
      <Typography
        variant='body2'
        color={pnlColor(pos.changePct)}
        sx={{ opacity: market.stale ? 0.45 : 1 }}
      >
        등락 {signed(pos.changePct)}%
      </Typography>
    )
  }

  /**
   * 미실현 손익 -- **심볼 줄에 하나다.**
   *
   * [미실현 축, 2026-09-05] 거래소가 one-way 넷으로 심볼당 하나를 주는 값이라 자리 개념이
   * 없다(`unrealizedOf` 머리글). 자리 줄 밑에 붙이면 "이 자리의 미실현"으로 읽히는데, 그건
   * 자리가 하나일 때만 맞는 말이었다.
   *
   * ⚠ **`hasPosition`을 받는 이유가 있다.** 포지션이 없으면 미실현이 없는 게 정상이라 아무
   * 말도 안 한다. 포지션이 있는데 값이 없으면 그건 **말해야 하는 상태**다 -- 진입 직후 최대
   * 1분, 또는 미결이 걸려 관측이 멈춘 구간이다(트레이더 `putUnrealized` 머리글).
   *
   * 옛 판은 이 자리에서 침묵했다. 대기 문구 조건에 `changePct == null`이 붙어 있어서 **등락이
   * 뜨는 동안에는 미실현이 왜 없는지 화면이 한마디도 안 했다.**
   */
  /** 미실현 옆 괄호 -- **축마다 이름을 붙여 나란히 둔다.**
   *
   * {{{
   *   % 잔고   dd 기준잔고 대비. 이 지갑이 얼마를 걸고 있나
   *   % 진입   거래소 평단 대비 mark. **이 심볼 순포지션의 등락**(2026-09-05 주인 지시)
   * }}}
   *
   * ⚠ **`% 진입`은 자리 줄의 `등락`과 기준가가 다르다** -- 저쪽은 자리 진입가 대비 1분봉 종가다.
   * 자리 둘의 등락 사이에 있는 것이 정상이다(명목 가중평균).
   *
   * 없는 축은 빼고 그린다. 둘 다 없으면 괄호 자체가 안 나온다 -- 빈 괄호는 "0" 으로 읽힌다. */
  const unrealRates = (unreal) => {
    const parts = []
    if (unreal.ratio != null) {
      parts.push(`${signed(unreal.ratio * 100)}% 잔고`)
    }
    if (unreal.changePct != null) {
      parts.push(`${signed(unreal.changePct)}% 진입`)
    }
    return parts.length === 0 ? '' : ` (${parts.join(' · ')})`
  }

  const renderUnrealized = (unreal, hasPosition) => {
    if (!hasPosition) {
      return null
    }
    if (!unreal.has) {
      return (
        <Typography variant='body2' color='text.secondary'>
          미실현 확인 중...
        </Typography>
      )
    }
    return (
      <Stack direction='row' spacing={1.5} alignItems='baseline' flexWrap='wrap'>
        <Typography
          variant='body2'
          color={pnlColor(unreal.pnl)}
          sx={{ opacity: unreal.stale ? 0.45 : 1 }}
        >
          {/* [분모 이름, 2026-08-31 주인 지시] 비율에 **분모 이름을 붙인다.** 한 줄에 축이 둘
              (잔고 대비 / 진입 대비)이고, 자리 줄의 `등락`까지 세면 셋이다. 이름이 없으면 같은
              축으로 읽혀 "등락 1.37%인데 미실현이 0.55%?"가 된다 -- 둘을 잇는 것은 명목/잔고
              비(= 카드 위의 계좌 레버리지)라 값이 다른 것이 정상인데, 화면이 그 말을 안 하고
              있었다. 분모가 dd 기준잔고라는 근거는 트레이더 `OrderManager.putUnrealized`. */}
          <b>미실현 {signed(unreal.pnl)}</b>
          {unrealRates(unreal)}
        </Typography>
        {/* 기준가가 둘이라 헷갈릴 자리다 -- 손익 쪽 기준가를 작게 붙여 둔다. */}
        {unreal.markPrice != null && (
          <Typography variant='caption' color='text.secondary'>
            mark {money(unreal.markPrice)}
            {unreal.at ? ` · ${ago(unreal.at)}` : ''}
          </Typography>
        )}
      </Stack>
    )
  }

  /**
   * 계좌 축 -- **지갑 값이다.** 안전망이 보는 그 레버리지이고(`LiveAccount`), 자리 다섯이
   * 같은 값을 싣는다.
   *
   * 여태 로그에만 있던 값이다. 확정본은 문턱이 10(진입 금지) / 20(전량 청산)인데 실측 최대가
   * 5.58배라, **여유가 줄고 있는지는 사고가 나기 전에 보여야 한다.**
   *
   * 못 쟀으면 숫자를 지어내지 않는다 -- 0으로 그리면 "레버리지 0배"와 구별이 안 된다.
   * 그때도 문턱 둘은 아는 값이라 같이 보여준다(무엇에 견줄 값인지가 사라지면 안 된다).
   */
  const renderAccount = (acct) => {
    if (!acct.has) {
      return (
        <Typography variant='body2' color='text.secondary'>
          계좌 레버리지 관측 대기
          {acct.maxLeverage != null ? ` (청산선 ${num(acct.maxLeverage, 1)}x)` : ''}
        </Typography>
      )
    }
    return (
      <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
        <Typography variant='body2' color={acct.blocked ? 'error.main' : undefined}>
          <b>계좌 레버리지 {num(acct.leverage, 2)}x</b>
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          진입금지 {num(acct.defendLeverage, 1)}x · 청산 {num(acct.maxLeverage, 1)}x
          {acct.headroom != null ? ` · 청산까지 ${num(acct.headroom, 2)}x` : ''}
        </Typography>
        {acct.blocked && (
          <Typography variant='caption' color='error.main'>
            진입 금지선을 넘었습니다 -- 새 진입이 막힙니다
          </Typography>
        )}
      </Stack>
    )
  }

  /**
   * 자리 하나 -- **심볼 한 줄.**
   *
   * [2단화, 2026-08-27] 한 지갑이 심볼 다섯을 돌고, 한 심볼에 자리가 둘까지 선다
   * (4h 껍질 + 1h 얹기). 그래서 화면도 2단이다 -- 지갑 카드 > 심볼 줄 > 자리 줄.
   *
   * **연결·정지·가동이 전부 이 층에 있다.** halt가 `wallet_symbols`로 내려갔고(30차)
   * 스냅샷도 심볼마다 한 장이라, 지갑 층에서 말할 수 있는 것은 "제일 나쁜 것"뿐이다.
   *
   * 뱃지를 **정상일 때는 안 그린다.** 다섯 줄에 '정상'이 다섯 번 서면 진짜 이상한 한 줄이
   * 그 안에 묻힌다 -- 여기서 눈에 띄어야 하는 것은 벗어난 자리다.
   */
  /**
   * 크기 손잡이 줄 -- **설정값이다. 관측값이 아니다.**
   *
   * 바로 윗줄(`renderAccount`)이 트레이더가 지금 재고 있는 값이라면 이 줄은 사람이 DB에
   * 적어 둔 값이다. 나란히 두는 이유가 그것이다 -- "이렇게 돌라고 적었는데 실제로 이렇게
   * 돌고 있다"를 한눈에 견주는 자리다.
   *
   * **트레이더가 죽어도 이 줄은 산다**(출처가 `wallets` 행이다). 그래서 위 두 줄과 달리
   * 흐리게 처리하지 않는다 -- 안 뜨는 지갑의 설정을 확인하는 것이 바로 그때 할 일이다.
   *
   * 캡 셋은 **꺼진 것도 그린다.** 35차의 "정상일 때 뱃지를 안 그린다"와 반대로 보이지만
   * 다른 부류다 -- 저건 상태 뱃지라 정상이 다섯 줄 서면 이상한 한 줄이 묻히고, 이건 설정이라
   * 안 그리면 "0이라서 안 뜬 것"과 "화면이 그 칸을 아직 모르는 것"이 구별되지 않는다.
   * 대신 꺼진 것은 흐리게 둬서 켜진 것이 눈에 든다.
   */
  const renderSizing = (sizing) => (
    <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
      <Typography variant='body2'>
        배수 {num(sizing.multiple, 2)} · 자리무게 {sizing.factor ?? '전 눈금 1.0'}
      </Typography>
      {sizing.caps.map((c) => (
        <Typography
          key={c.label}
          variant='caption'
          color={c.on ? undefined : 'text.secondary'}
        >
          {c.label} {c.on ? capText(c) : '끔'}
        </Typography>
      ))}
    </Stack>
  )

  /**
   * 국면 한 줄. **`s`와 표 수를 같이 낸다.**
   *
   * 배율만 그리면 `1.33`이 어디서 왔는지 카드에서 못 읽는다. 실제로 그 물음이 나왔다 --
   * *"국면이 롱이면 롱진입 1.5 숏진입 0.5 고정된 거 아니었나?"* `1.5/0.5`는 자 여섯이
   * **전부** 위일 때 하나이고 다섯이면 `1.333/0.667`이다. `5↑1↓`이 그 답을 준다.
   *
   * 자를 안 지은 지갑(`k = 0`)은 **줄 자체를 안 그린다** -- "×1.00"으로 그리면 꺼진 것과
   * 켜져 있는데 중립인 것이 같은 글자가 된다. 안 뜨는 것이 곧 "이 지갑은 국면을 안 본다"다.
   */
  const renderRegime = (rg) => {
    if (!rg.has) return null
    const sign = rg.s > 0 ? '+' : ''
    return (
      <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
        <Typography variant='body2' color='text.secondary'>
          국면 {sign}{num(rg.s, 2)} ({rg.up}↑{rg.down}↓)
          {' · '}롱 ×{num(rg.longFactor, 2)}
          {' · '}숏 ×{num(rg.shortFactor, 2)}
        </Typography>
        {rg.warming && (
          <Typography variant='caption' color='text.secondary'>
            자 {rg.up + rg.down}/{rg.rulers} 예열 중
          </Typography>
        )}
      </Stack>
    )
  }

  const renderSymbol = (w, sy, nowMs) => {
    const { snap, link, halt } = sy
    const dim = link !== Link.OK
    const market = marketOf(snap, nowMs)
    const regime = regimeOf(snap)
    const positions = positionsOf(snap)
    const unreal = unrealizedOf(snap, nowMs)
    const settle = settlementOf(snap, nowMs)

    return (
      <div
        key={sy.key}
        style={{ marginTop: 8, paddingLeft: 10, borderLeft: '2px solid rgba(128,128,128,0.25)' }}
      >
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          {/* [이름 붙이기, 2026-08-27 주인 지시] `# 3` -> `# 3 (ETHUSDT)`.
              사전을 못 받았으면 `# 3` 으로 돌아간다 -- 지금 화면 그대로다. */}
          <Typography variant='body2'><b>{symbolTextOf(sy.symbolId)}</b></Typography>
          {link !== Link.OK && (
            <Chip size='small' label={LinkLabel[link]} color={linkColor(link)} variant='outlined' />
          )}
          {/* [2단 게이트, 2026-08-27] 자리 가동 상태 -- **읽기와 쓰기를 같은 자리에 둔다**
              (지갑 칩과 같은 규약). 목록을 못 받았으면 화살표를 떼고 읽기 전용으로 돌아간다.

              ⚠ **ACTIVE 일 때도 그린다** -- 옆의 연결·정지 뱃지와 달리 이건 조작 창구라,
              정상일 때 숨기면 "여기서 바꿀 수 있다" 를 아무도 모른다. 대신 색을 안 준다. */}
          {statuses.length === 0
            ? (sy.run !== RUN_ACTIVE && (
              <Chip size='small' label={RunLabel[sy.run] ?? sy.run} color='warning' variant='outlined' />
            ))
            : (
              <Chip
                size='small'
                color={sy.run === RUN_ACTIVE ? 'default' : 'warning'}
                variant='outlined'
                disabled={busyId === `${w.walletId}/${sy.symbolId}`}
                onClick={(e) => set_menu({
                  el: e.currentTarget, walletId: w.walletId, symbolId: sy.symbolId,
                })}
                sx={{ cursor: 'pointer' }}
                label={
                  <Stack direction='row' alignItems='center' component='span'>
                    {RunLabel[sy.run] ?? sy.run}
                    <ArrowDropDownIcon sx={{ fontSize: 16, ml: 0.25, mr: -0.75 }} />
                  </Stack>
                }
              />
            )}
          {halt.halted && <Chip size='small' label='정지' color='error' />}
          {snap && dim && (
            <Typography variant='caption' color='text.secondary'>
              마지막 수신 {ago(snap.uptAt)}
            </Typography>
          )}
        </Stack>

        <div style={{ opacity: dim ? 0.45 : 1 }}>
          {/* 시세는 **포지션과 무관하게** 뜬다 -- 심볼의 사실이라 스냅샷 머리에 실려 온다.
              그래서 플랫인 자리에서도 "지금 얼마인가"를 볼 수 있다. */}
          <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
            <Typography variant='body2' sx={{ opacity: market.stale ? 0.45 : 1 }}>
              시세 {money(market.price)}
            </Typography>
            {market.at && (
              <Typography variant='caption' color='text.secondary'>
                {ago(market.at)} 봉 마감{market.stale ? ' · 수집 지연' : ''}
              </Typography>
            )}
          </Stack>

          {/* 국면은 **시세와 같은 성질**이라 여기 붙는다 -- 심볼의 사실이고 포지션이 없어도 뜬다.
              크기를 정하는 항이라 진입 수량을 보기 **전에** 읽혀야 한다. */}
          {renderRegime(regime)}

          {/* 🔴 자리마다 한 줄이다. 옛 화면은 첫 자리만 그려서 **얹기가 든 동안 노출을 절반으로
              보여줬다** -- 그게 하필 크기가 두 배로 늘어난 구간이라 제일 나쁜 방향의 거짓말이었다. */}
          {positions.length === 0
            ? <Typography variant='body2'>포지션 없음</Typography>
            : positions.map((pos, idx) => (
              <div key={`${sy.key}/${pos.signalTf ?? idx}`} style={{ marginTop: 2 }}>
                {renderPosition(pos)}
                {renderChange(market, pos)}
              </div>
            ))}

          {/* [미실현 축, 2026-09-05] **자리 줄 밖이다 -- 자리들 바로 밑에 한 줄.** 심볼 하나의
              합이라 위 자리 줄들을 접어서 말하는 값이고, 읽는 순서가 자리 -> 합이어야 한다.
              포지션이 없으면 아무것도 안 그린다(그 판정은 `renderUnrealized`가 진다). */}
          {renderUnrealized(unreal, positions.length > 0)}

          <Typography variant='body2'>
            미결 주문 {snap?.orders?.length ?? 0}건
          </Typography>
        </div>

        {renderSettled(settle)}
        {halt.halted && renderHalt(w, sy)}
      </div>
    )
  }

  /**
   * 지갑 하나 -- **카드 머리(지갑 값) + 자리 줄들.**
   *
   * 카드에 남는 것은 **지갑 축의 사실**뿐이다: 이름/run_mode, 가동 상태, 잔고·dd, 계좌
   * 레버리지. 시세·포지션·미결·정지는 전부 심볼 줄로 내려갔다(`renderSymbol`).
   *
   * **배선이 정상이 아니면 숫자를 흐리게 한다.** 서버가 낡은 스냅샷도 거르지 않고 보내므로
   * (그래야 웹이 "대상 없음"과 "갱신 멈춤"을 구분할 수 있다) 낡은 값이 실시간처럼 보이지
   * 않게 하는 건 화면 몫이다. 지우지는 않는다 -- 마지막으로 알던 값이 사고 조사에 필요하다.
   */
  const renderWallet = (w) => {
    // run은 renderRun이 w에서 직접 꺼낸다 -- 여기서 또 꺼내면 안 쓰는 이름이 남는다.
    const { row, snap, link, symbols } = w
    const dim = link !== Link.OK
    const nowMs = Date.now()

    // 계좌 값은 자리 아무 하나에서 읽는다(`mergeWallets`가 신선한 것을 골라 `w.snap`에 둔다).
    const acct = accountOf(snap)

    // 가동 라벨의 '종료됨'은 **자리가 전부 정리된 뒤**에만 맞다. 하나라도 남아 있으면
    // 아직 정리 중이고, 그 차이가 사람에게는 "손대도 되는가"의 차이다.
    const walletSettle = {
      settled: symbols.length > 0
        && symbols.every((sy) => settlementOf(sy.snap, nowMs).settled),
    }

    return (
      <div key={w.walletId} style={{ marginBottom: 16 }}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          <Typography variant='subtitle1'><b>{row.name}</b></Typography>
          {/* `symbolId`/`tf`가 여기 있었다 -- 30차에 `wallets`에서 없어졌다(지갑 하나가
              심볼 여럿을 돈다). 그 자리는 아래 심볼 줄들이 진다. */}
          <Typography variant='caption'>{row.runMode} · 자리 {symbols.length}</Typography>
          <Chip size='small' label={LinkLabel[link]} color={linkColor(link)} variant='outlined' />
          {renderRun(w, walletSettle)}
          {w.haltedCount > 0 && (
            <Chip size='small' label={`정지 ${w.haltedCount}`} color='error' />
          )}
        </Stack>

        <div style={{ opacity: dim ? 0.45 : 1 }}>
          {/* [2026-08] 잔고는 **`wallets.balance`(잔고 미러)**다 -- 트레이더가 거래소를 부른
              자리마다(기동·진입·청산·60분 스냅샷) 적어두는 실잔고다.

              옛 화면은 여기에 **dd 기준잔고**를 그렸다. 그건 잔고가 아니라 측정 앵커라서
              첫 진입 전에는 값 자체가 없었고, 입출금은 다음 진입까지 안 보였다. 축을 갈랐다.

              **기준 시각을 안 붙인다.** 잔고는 체결이 있을 때만 변하고 위 네 자리가 그
              사건을 전부 덮으므로, 낡는 게 아니라 변할 일이 없어서 그대로인 값이다.

              ⚠ **`0`은 "계좌가 비었다"는 사실이다.** 미기동 지갑은 스냅샷 자체가 없어
              이 갈래에 안 들어온다. 여기서 보는 0은 거래소에 물어서 받아온 0이다. */}
          <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
            <Typography variant='body2'>
              잔고 {money(snap?.balance)} / dd {num(snap?.ddRatio)}
            </Typography>
            {snap && snap.balance === 0 && (
              <Typography variant='caption' color='error.main'>
                계좌가 비었습니다
              </Typography>
            )}
          </Stack>

          {renderAccount(acct)}
        </div>

        {/* [지갑 크기 칸, 2026-08-28 주인 지시] 옛 판은 이 셋이 `user_symbol_configs`에 있어
            지갑 화면에서 안 보였다 -- 크기를 정하는 값인데 크기를 보는 화면에 없었다.

            ⚠ **흐리게 처리하는 블록 밖에 둔다.** 저 `opacity`는 "이 값이 낡았을 수 있다"는
            뜻이고, 이 줄의 출처는 스냅샷이 아니라 `wallets` 행이라 낡지 않는다. 안에 넣으면
            트레이더가 죽었을 때 **설정까지 못 믿을 값처럼 보인다** -- 그때가 설정을 확인하는
            바로 그 순간이다. */}
        {renderSizing(sizingOf(row))}

        {/* 자리 줄. **DB가 정본이라 미기동 심볼도 여기 선다** -- 정지된 채 트레이더가 죽은
            자리야말로 사람이 봐야 하는 것이다. */}
        {symbols.length === 0
          ? (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              이 지갑에 등록된 자리가 없습니다 -- `wallet_symbols` 행이 필요합니다
            </Typography>
          )
          : symbols.map((sy) => renderSymbol(w, sy, nowMs))}

        <Divider sx={{ mt: 1 }} />
      </div>
    )
  }

  return (
    <>
      {userId && (
        <Stack direction='row' alignItems='center' spacing={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackOutlinedIcon />
          </IconButton>
          <Typography variant='body2'>userId {userId} 의 지갑</Typography>
        </Stack>
      )}

      {/* 층이 다른 세 가지 실패를 각각 말한다. 하나로 합치면 어디를 고쳐야 할지가 사라진다. */}
      {!connected && (
        <Alert severity='warning' sx={{ mb: 1 }}>
          브로커 연결 끊김 -- 실시간 값이 갱신되지 않습니다(트레이더는 정상일 수 있습니다)
        </Alert>
      )}
      {serviceError && (
        <Alert severity='error' sx={{ mb: 1 }}>트레이더 오류: {serviceError}</Alert>
      )}
      {error && (
        <Alert severity='error' sx={{ mb: 1 }}>{error}</Alert>
      )}

      {/* DB에 없는데 스냅샷이 오는 지갑. 지운 지갑을 트레이더가 아직 돌리고 있다는 뜻이라
          목록에서 조용히 빼면 안 된다 -- 실거래가 도는데 아무 화면에도 안 나온다. */}
      {orphans.length > 0 && (
        <Alert severity='error' sx={{ mb: 1 }}>
          목록에 없는 지갑이 가동 중입니다 (walletId {orphans.map((o) => o.walletId).join(', ')})
        </Alert>
      )}

      {loading && <Typography variant='body2'>불러오는 중...</Typography>}
      {!loading && wallets.length === 0 && (
        <Typography variant='body2'>등록된 지갑이 없습니다</Typography>
      )}

      {/* 확정 잔고 합.
          **"총자산"이라 부르지 않는다.** 포지션 보유 중의 평가손익이 안 들어가 있어서다
          (`totalsOf` 주석). 이름이 값보다 앞서 나가면 사람이 이 숫자로 손익을 읽는다.

          선물과 현물을 갈라 적는 이유도 같다 -- 합쳐서 하나만 내면 `savingRatio`로 돈이
          마스터에 쌓이는 게 안 보이고, 서브 합이 줄어든 것이 손실인지 이체인지 구별되지
          않는다. 그 구별이 이 화면을 만든 이유다. */}
      {!loading && wallets.length > 0 && (
        <div>
          <Typography variant='body2'>
            <b>확정 잔고 합 {money(totals.total)}</b>
            {/* 관측이 안 된 마스터가 있으면 이 합은 **덜 세어진 값**이다. 숫자 옆에 붙여야
                한다 -- 안 붙이면 사람이 완전한 값으로 읽는다. */}
            {totals.spotPending > 0 && (
              <Typography component='span' variant='caption' color='warning.main'>
                {' '}-- 마스터 {totals.spotPending}개 관측 대기
              </Typography>
            )}
          </Typography>

          {/* 🔴 **거래소가 축이다.** 돈이 어디 있는지가 곧 "어느 거래소의 어느 계좌"라서,
              선물/현물로 먼저 가르면 두 거래소가 한 숫자에 섞인다(실제로 그랬다 -- Bitget
              지갑이 붙은 뒤에도 "선물 366.11" 한 줄이라 어느 쪽이 비었는지 안 보였다).

              거래소가 하나뿐이어도 줄을 그린다 -- 위 총합과 값이 같아 보여도 **괄호 안의
              갈래(마스터/거래지갑)가 그 줄의 내용**이라 접을 것이 없다. */}
          {totals.byExchange.map((e) => (
            <Stack key={e.exchangeId} direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
              <Typography variant='body2'>
                {labelOfExchange(e.exchangeId)} <b>{money(e.total)}</b>
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                (마스터 {e.masterCount === 0
                  ? '없음'
                  : (e.spot == null ? '관측 대기' : money(e.spot))}
                {' · '}
                거래지갑 {money(e.futures)} · 지갑 {e.walletCount}개)
              </Typography>
            </Stack>
          ))}

          {/* 마스터 줄 -- **금액을 다시 안 적는다.** 위 거래소 줄이 이미 말했고, 같은 숫자를
              두 번 적으면 사람이 다른 값인 줄 알고 대조한다. 여기 남는 것은 위 줄이 못 하는
              말들이다: 계정 이름, 마지막으로 값이 **바뀐** 시각, 그리고 잠김/Earn 어긋남.

              세 상태를 갈라 말한다 -- 계좌 없음 / 관측 대기 / 정상. 하나로 접으면 "마스터를
              안 만들었다"와 "관측이 안 돌고 있다"가 같은 빈칸이 되는데, 뒤쪽은 고쳐야 하는
              사건이다(`MasterBalanceFetcher`가 멎었거나, 그 거래소를 아직 안 본다). */}
          {totals.masters.map((master) => (
            <Stack key={master.account.id} direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
              <Typography variant='caption' color='text.secondary'>
                마스터 {labelOfExchange(master.account.exchangeId)} · {master.account.name}
              </Typography>
              {master.balance ? (
                <>
                  {/* "관측"이 아니라 **최종**이다. 마스터는 값이 안 바뀌면 행을 안 만들어서
                      (`MasterBalanceService.addBalance`) 이 시각은 마지막으로 **값이 바뀐**
                      때다. 관측 자체는 5분마다 계속 돈다 -- "관측"이라 쓰면 그동안 안 본 것처럼
                      읽힌다. */}
                  <Typography variant='caption' color='text.secondary'>
                    {ago(master.balance.regAt)} 최종
                  </Typography>
                  {/* balance와 free가 벌어져 있으면 주문에 잠겼거나 Earn에 들어간 돈이다.
                      정상이면 둘이 같아서 이 칩은 안 뜬다 -- 뜨는 것 자체가 신호다. */}
                  {master.balance.balance !== master.balance.free && (
                    <Typography variant='caption' color='warning.main'>
                      가용 {money(master.balance.free)} -- 잠김/Earn {money(master.balance.balance - master.balance.free)}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant='caption' color='warning.main'>
                  관측 대기 -- 잔고 기록이 아직 없습니다
                </Typography>
              )}
            </Stack>
          ))}

          {/* 총액 **밖**의 몫 -- 지금은 테스트넷.
              버리지 않고 따로 내는 이유: 청산·이체 시험 결과를 보려면 그 잔고도 봐야 한다.
              **"합계에 안 들어감"을 글자로 적는 게 짝이다** -- 숫자만 나란히 두면 사람이
              눈으로 더한다. 실제로 이 줄이 없던 동안 테스트넷 잔고가 합계에 섞여 실계좌가
              비었다는 사실을 가리고 있었다.
              해당 지갑이 하나도 없으면 줄 자체가 안 뜬다(판정은 `totalsOf`가 한다). */}
          {totals.excluded.count > 0 && (
            <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
              <Typography variant='caption' color='text.secondary'>
                {totals.excluded.modes.join('/')} {money(totals.excluded.futures)}
                {' '}(지갑 {totals.excluded.count}개)
                {totals.excluded.byExchange.length > 1 && (
                  ` -- ${totals.excluded.byExchange
                    .map((e) => `${labelOfExchange(e.exchangeId)} ${money(e.futures)}`)
                    .join(' · ')}`
                )}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                -- 합계에 안 들어감
              </Typography>
            </Stack>
          )}
          <Divider sx={{ mt: 1, mb: 1 }} />
        </div>
      )}

      {wallets.map(renderWallet)}

      {/* 가동상태 메뉴 -- 지갑마다 따로 두지 않고 **하나를 돌려 쓴다**. 어차피 한 번에 하나만
          열리고, 지갑 수만큼 Menu를 그리면 목록이 길어질수록 그만큼 무거워진다.
          현재 값은 선택 표시로만 알린다 -- 항목을 지우면 "지금 뭐였지"를 되짚을 자리가 없다. */}
      <Menu open={Boolean(menu)} anchorEl={menu?.el} onClose={closeMenu}>
        {statuses.map((s) => (
          <MenuItem
            key={s}
            dense
            selected={s === menuRun}
            onClick={() => pickStatus(s)}
          >
            {/* 서버가 허용값을 늘렸는데 화면에 RunLabel이 없으면 원문 코드를 그대로 보여준다.
                빈 칸으로 두면 "값이 없다"로 읽힌다. */}
            {RunLabel[s] || s}
          </MenuItem>
        ))}
      </Menu>

      {/* 확인 창.
          **셋 다 확인을 받되 FIN만 빨갛다.** 문구까지 같게 두면 확인이 요식이 되어 정작
          청산에서 안 멈춘다. 문구와 위험 판정은 statusChangeIntent가 만든다 -- 여기서
          조건을 다시 보기 시작하면 그 규칙이 둘로 갈린다. */}
      <Dialog open={Boolean(pending)} onClose={closePending}>
        <DialogTitle>{pending?.intent.title}</DialogTitle>
        <DialogContent>
          {/* 🔴 **무엇이 대상인가를 맨 위에 적는다.** 같은 창이 지갑과 자리 둘 다에 뜨는데,
              FIN 문구가 "전부 종료합니다"라 범위가 없으면 자리 하나를 끄면서 지갑을 비우는
              줄 안다. 되돌릴 수 없는 조작에서 제일 비싼 오해다. */}
          {pending?.scope && (
            <Typography variant='body2' sx={{ mb: 1 }}>
              <b>{pending.scope}</b>
            </Typography>
          )}
          {pending?.intent.lines.map((line, i) => (
            <DialogContentText
              key={i}
              // 첫 줄이 "무슨 일이 벌어지는가"고 나머지는 단서다. 파괴적 변경은 그 첫 줄을
              // 붉게 세운다 -- 사람이 제목만 보고 누르는 걸 한 번 더 붙잡는다.
              color={i === 0 && pending?.intent.destructive ? 'error' : 'text.primary'}
              sx={{ mt: i === 0 ? 0 : 1 }}
            >
              {line}
            </DialogContentText>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePending}>취소</Button>
          <Button
            color={pending?.intent.destructive ? 'error' : 'primary'}
            onClick={confirmPending}
          >
            {pending?.intent.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default WalletPage
